import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Music, List, ThumbsUp, XCircle } from 'lucide-react';
import axios from 'axios';
import { getInitialLanguage, setLanguage, t } from '@/i18n';
import LanguageSelector from '@/components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const djName = searchParams.get('dj') || 'DJ';
  const djId = searchParams.get('id') || '';
  const modeParam = searchParams.get('mode') || 'pro';
  const eventSlug = searchParams.get('event') || '';
  
  const [lang, setLang] = useState(getInitialLanguage);
  const [eventStatus, setEventStatus] = useState(null);
  const [eventName, setEventName] = useState('');
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!djId || !eventSlug) {
      setLoading(false);
      return;
    }
    
    axios.get(`${API}/requests/${djId}/public?event=${eventSlug}`)
      .then(res => {
        const event = res.data.event;
        if (event) {
          setEventStatus(event.status);
          setEventName(event.name || '');
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [djId, eventSlug]);

  const handleLangChange = (newLang) => {
    setLang(newLang);
    setLanguage(newLang);
  };

  const buildRequestUrl = () => {
    const params = new URLSearchParams({ mode: modeParam, lang, view: 'list' });
    if (eventSlug) params.set('event', eventSlug);
    return `/request/${djId}?${params.toString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-neon-cyan animate-pulse font-heading text-xl">Chargement...</div>
      </div>
    );
  }

  const bgStyle = {
    backgroundImage: "linear-gradient(to bottom, rgba(5,5,5,0.95), rgba(5,5,5,0.98)), url('https://images.unsplash.com/photo-1578300253266-dedd2cd40912?w=1200')",
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  // ========== ÉVÉNEMENT TERMINÉ ==========
  if (eventStatus === 'closed') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 relative" style={bgStyle}>
        <div className="absolute top-4 right-4 z-10">
          <LanguageSelector currentLang={lang} onChangeLang={handleLangChange} />
        </div>
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-24 h-24 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-14 h-14 text-gray-400" />
          </div>
          <h2 className="font-heading text-2xl text-white mb-6">{t('success_closed_title', lang)}</h2>
          <div className="bg-white/5 rounded-xl p-5 mb-8 border border-white/10">
            <p className="text-gray-300 leading-relaxed">{t('success_closed_text', lang)}</p>
          </div>
          <p className="text-gray-600 text-xs">{t('success_closed_thanks', lang)}</p>
        </div>
      </div>
    );
  }

  // ========== PRÉ-ÉVÉNEMENT (DRAFT) ==========
  if (eventStatus === 'draft') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 relative" style={bgStyle}>
        <div className="absolute top-4 right-4 z-10">
          <LanguageSelector currentLang={lang} onChangeLang={handleLangChange} />
        </div>
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-24 h-24 bg-neon-pink/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-14 h-14 text-neon-pink" />
          </div>
          <h2 className="font-heading text-2xl text-white mb-6" data-testid="pre-event-title">
            {t('success_pre_event_title', lang)}
          </h2>
          <div className="bg-gradient-to-br from-neon-pink/10 to-neon-cyan/10 rounded-xl p-5 mb-6 border border-neon-pink/20">
            <p className="text-gray-300 leading-relaxed">{t('success_pre_event_text', lang)}</p>
          </div>
          {eventName && (
            <div className="bg-white/5 rounded-lg px-4 py-2 mb-6 inline-block">
              <span className="text-neon-cyan text-sm">🎵 {eventName}</span>
            </div>
          )}
          <div className="space-y-4">
            <Link
              to={buildRequestUrl()}
              className="flex items-center justify-center gap-2 bg-neon-pink text-white font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(255,0,128,0.6)] rounded-full px-8 py-4 w-full transition-all"
              data-testid="view-like-requests-btn"
            >
              <ThumbsUp className="w-5 h-5" />
              {t('success_pre_event_cta', lang)}
            </Link>
            <p className="text-neon-cyan text-sm font-medium">{t('success_pre_event_subtitle', lang)}</p>
          </div>
          <p className="text-gray-600 text-xs mt-8">{t('success_pre_event_thanks', lang)}</p>
        </div>
      </div>
    );
  }

  // ========== STANDARD (ACTIVE OU PAS D'ÉVÉNEMENT) ==========
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 relative" style={bgStyle}>
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector currentLang={lang} onChangeLang={handleLangChange} />
      </div>
      <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
        <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-14 h-14 text-green-500" />
        </div>
        <h2 className="font-heading text-2xl text-white mb-6">{t('success_title', lang)}</h2>
        <div className="bg-white/5 rounded-xl p-5 mb-8 border border-white/10">
          <p className="text-gray-300 leading-relaxed">{t('success_text', lang)}</p>
        </div>
        <p className="text-gray-400 text-sm mb-8">
          <Music className="w-4 h-4 inline mr-2 text-neon-cyan" />
          {t('success_dj_label', lang)} : <span className="text-neon-cyan font-semibold">{djName}</span>
        </p>
        <div className="space-y-4">
          <Link
            to={buildRequestUrl()}
            className="flex items-center justify-center gap-2 bg-neon-cyan text-black font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] rounded-full px-8 py-4 w-full transition-all"
            data-testid="view-requests-btn"
          >
            <List className="w-5 h-5" />
            {t('success_cta', lang)}
          </Link>
          <p className="text-gray-500 text-xs">{t('success_subtitle', lang)}</p>
        </div>
        <p className="text-gray-600 text-xs mt-8">{t('success_thanks', lang)}</p>
      </div>
    </div>
  );
}
